// components/vcard/HomeInfo.tsx
import React, { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import type { VCard, MessagingContact } from "../../types/vcard";
import { useVCardProfileSync } from "../../hooks/useVCardProfileSync";
import OwnerSection from "./shared/OwnerSection";
import { useUserStore } from "../../store/userStore";
import type { UserProfile } from "../../types/user";
import AddressAutocompleteInput from "../ui/AddressAutocompleteInput";
import type { ResolvedAddress } from "../../services/address/googlePlaces";

const getCountryCode = (value: unknown, fallback: string): string =>
  typeof value === "string" && value.trim().length > 0 ? value : fallback;

const pickDefined = <T,>(primary: T | undefined, fallback: T): T =>
  primary !== undefined ? primary : fallback;

interface HomeInfoProps {
  formData: Partial<VCard>;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  isSubmitting?: boolean;
  showSaveConfirmation?: boolean;
  onToggle: () => void;
  isActive: boolean;
  isLoading?: boolean;
}

const HomeInfo: React.FC<HomeInfoProps> = ({
  formData,
  handleChange,
  handleSubmit,
  isSubmitting = false,
  showSaveConfirmation = false,
  onToggle,
  isActive,
  isLoading = false,
}) => {
  const [mobilePhoneCountryCode, setMobilePhoneCountryCode] = useState("+61");
  const [workPhoneCountryCode, setWorkPhoneCountryCode] = useState("+61");
  const [travelPhoneCountryCode, setTravelPhoneCountryCode] = useState("+61");

  const profile = useUserStore((state) => state.profile);
  const updateProfile = useUserStore((state) => state.updateProfile);

  void onToggle;
  void isActive;
  void isLoading;

  // Keep home fields in sync with profile/vCard changes.
  const { vcard: homeVCard } = useVCardProfileSync("home", {
    syncFields: [
      "firstName",
      "lastName",
      "email",
      "mobilePhone",
      "workEmail",
      "workPhone",
      "travelPhone",
      "messagingContact",
      "messagingContacts",
    ],
  });

  const homeVCardRecord = (homeVCard ?? {}) as Record<string, unknown>;
  const formDataRecord = formData as Record<string, unknown>;

  const syncedMobilePhoneCountryCode = getCountryCode(
    formDataRecord.mobilePhoneCountryCode ??
      homeVCardRecord.mobilePhoneCountryCode,
    mobilePhoneCountryCode,
  );
  const syncedWorkPhoneCountryCode = getCountryCode(
    formDataRecord.workPhoneCountryCode ?? homeVCardRecord.workPhoneCountryCode,
    workPhoneCountryCode,
  );
  const syncedTravelPhoneCountryCode = getCountryCode(
    formDataRecord.travelPhoneCountryCode ??
      homeVCardRecord.travelPhoneCountryCode,
    travelPhoneCountryCode,
  );

  const syncedFormData = {
    ...formData,
    firstName: pickDefined(formData.firstName, homeVCard?.firstName || ""),
    lastName: pickDefined(formData.lastName, homeVCard?.lastName || ""),
    middleName: (formData as Partial<VCard> & { middleName?: string })
      .middleName,
    email: pickDefined(
      formData.email,
      homeVCard?.email || homeVCard?.personalEmail || formData.personalEmail,
    ),
    workEmail: pickDefined(formData.workEmail, homeVCard?.workEmail || ""),
    mobilePhone: pickDefined(
      formData.mobilePhone,
      homeVCard?.mobilePhone || "",
    ),
    mobilePhoneCountryCode: syncedMobilePhoneCountryCode,
    workPhone: pickDefined(formData.workPhone, homeVCard?.workPhone || ""),
    workPhoneCountryCode: syncedWorkPhoneCountryCode,
    travelPhone: pickDefined(
      formData.travelPhone,
      homeVCard?.travelPhone || "",
    ),
    travelPhoneCountryCode: syncedTravelPhoneCountryCode,
    messagingContacts: Array.isArray(formData.messagingContacts)
      ? formData.messagingContacts
      : Array.isArray(homeVCard?.messagingContacts)
        ? homeVCard.messagingContacts
        : Array.isArray(profile?.messagingContacts)
          ? profile.messagingContacts
          : [],
    messagingContact:
      formData.messagingContact !== undefined
        ? formData.messagingContact
        : homeVCard?.messagingContact ||
          profile?.messagingContact ||
          formData.messagingContacts?.[0] ||
          homeVCard?.messagingContacts?.[0] ||
          profile?.messagingContacts?.[0],
  };

  const emitFieldChange = (name: string, value: string): void => {
    handleChange({
      target: {
        name,
        value,
      },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleResolvedHomeAddress = (
    resolvedAddress: ResolvedAddress,
  ): void => {
    const updates: Array<{ name: string; value: string }> = [
      { name: "address.street", value: resolvedAddress.line1 },
      { name: "address.city", value: resolvedAddress.city },
      { name: "address.state", value: resolvedAddress.state },
      { name: "address.zip", value: resolvedAddress.postalCode },
      { name: "address.country", value: resolvedAddress.country },
    ];

    updates.forEach((update) => {
      if (update.value.trim()) {
        emitFieldChange(update.name, update.value);
      }
    });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void (async () => {
          await handleSubmit(e);
          if (!profile) {
            return;
          }

          const profileUpdates: Partial<UserProfile> = {};
          const formFirstName = (formData.firstName as string) || "";
          const formLastName = (formData.lastName as string) || "";
          const formEmail = (formData.email as string) || "";
          const formWorkEmail = (formData.workEmail as string) || "";
          const formMobilePhone = (formData.mobilePhone as string) || "";
          const formWorkPhone = (formData.workPhone as string) || "";
          const formTravelPhone = (formData.travelPhone as string) || "";
          const formAddress = formData.address as
            | UserProfile["address"]
            | undefined;

          if (formFirstName && formFirstName !== profile.firstName) {
            profileUpdates.firstName = formFirstName;
          }

          if (formLastName && formLastName !== profile.lastName) {
            profileUpdates.lastName = formLastName;
          }

          if (formEmail && formEmail !== profile.email) {
            profileUpdates.email = formEmail;
          }

          if (formWorkEmail !== (profile.alternativeEmail || "")) {
            profileUpdates.alternativeEmail = formWorkEmail;
          }

          if (formMobilePhone && formMobilePhone !== profile.phone) {
            profileUpdates.phone = formMobilePhone;
          }

          if (formWorkPhone !== (profile.workPhone || "")) {
            profileUpdates.workPhone = formWorkPhone;
          }

          if (formTravelPhone !== (profile.travelPhone || "")) {
            profileUpdates.travelPhone = formTravelPhone;
          }

          const normalizeMessagingContacts = (
            contacts: Array<MessagingContact | null | undefined>,
          ): MessagingContact[] =>
            contacts
              .filter((contact): contact is MessagingContact =>
                Boolean(contact && contact.app),
              )
              .map((contact) => ({
                app: contact.app,
                handle: contact.handle || "",
              }));

          const submittedMessagingContacts = normalizeMessagingContacts(
            Array.isArray(formData.messagingContacts)
              ? formData.messagingContacts
              : formData.messagingContact
                ? [formData.messagingContact]
                : [],
          );
          const currentMessagingContacts = normalizeMessagingContacts(
            Array.isArray(profile.messagingContacts)
              ? profile.messagingContacts
              : profile.messagingContact
                ? [profile.messagingContact]
                : [],
          );
          const submittedPrimaryMessagingContact = formData.messagingContact
            ?.app
            ? {
                app: formData.messagingContact.app,
                handle: formData.messagingContact.handle || "",
              }
            : submittedMessagingContacts[0] || null;
          const currentPrimaryMessagingContact = profile.messagingContact?.app
            ? {
                app: profile.messagingContact.app,
                handle: profile.messagingContact.handle || "",
              }
            : currentMessagingContacts[0] || null;
          if (
            JSON.stringify(submittedMessagingContacts) !==
              JSON.stringify(currentMessagingContacts) ||
            JSON.stringify(submittedPrimaryMessagingContact) !==
              JSON.stringify(currentPrimaryMessagingContact)
          ) {
            profileUpdates.messagingContacts = submittedMessagingContacts;
            profileUpdates.messagingContact = submittedPrimaryMessagingContact;
          }

          if (formAddress) {
            const nextAddress = {
              street: formAddress.street || "",
              unit: formAddress.unit || "",
              city: formAddress.city || "",
              state: formAddress.state || "",
              zip: formAddress.zip || "",
              country: formAddress.country || "",
            };
            const currentAddress = profile.address || {};
            if (
              JSON.stringify(nextAddress) !== JSON.stringify(currentAddress)
            ) {
              profileUpdates.address = nextAddress;
            }
          }

          if (Object.keys(profileUpdates).length > 0) {
            await updateProfile(profileUpdates);
          }
        })();
      }}
      className="space-y-6"
    >
      {showSaveConfirmation && (
        <div className="flex items-center space-x-2 rounded-lg bg-green-50 p-4 text-green-700">
          <CheckCircle2 className="h-5 w-5" />
          <span>Changes saved successfully!</span>
        </div>
      )}

      <OwnerSection
        formData={syncedFormData}
        handleChange={handleChange}
        mobilePhoneCountryCode={syncedFormData.mobilePhoneCountryCode}
        setMobilePhoneCountryCode={setMobilePhoneCountryCode}
        workPhoneCountryCode={syncedFormData.workPhoneCountryCode}
        setWorkPhoneCountryCode={setWorkPhoneCountryCode}
        travelPhoneCountryCode={syncedFormData.travelPhoneCountryCode}
        setTravelPhoneCountryCode={setTravelPhoneCountryCode}
      />

      {/* Optional home-only field kept separate while owner/contact fields use shared OwnerSection */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Middle Name
        </label>
        <input
          type="text"
          name="middleName"
          value={
            typeof syncedFormData.middleName === "string"
              ? syncedFormData.middleName
              : ""
          }
          onChange={handleChange}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-2 focus:ring-red-500"
        />
      </div>

      <div>
        <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-gray-100">
          Home Address
        </h3>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Street Address*
            </label>
            <AddressAutocompleteInput
              name="address.street"
              value={syncedFormData.address?.street || ""}
              onChange={(nextValue) => {
                emitFieldChange("address.street", nextValue);
              }}
              onResolvedAddress={handleResolvedHomeAddress}
              required
              inputClassName="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-2 focus:ring-red-500"
              placeholder="Start typing your home address"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Unit, Suite, Apartment, etc.
            </label>
            <input
              type="text"
              name="address.unit"
              value={syncedFormData.address?.unit || ""}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                City*
              </label>
              <input
                type="text"
                name="address.city"
                value={syncedFormData.address?.city || ""}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                State/Province*
              </label>
              <input
                type="text"
                name="address.state"
                value={syncedFormData.address?.state || ""}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                ZIP/Postal Code*
              </label>
              <input
                type="text"
                name="address.zip"
                value={syncedFormData.address?.zip || ""}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Country*
              </label>
              <input
                type="text"
                name="address.country"
                value={syncedFormData.address?.country || ""}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-gray-100">
          Additional Information
        </h3>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Notes
            </label>
            <textarea
              name="note"
              value={syncedFormData.note || ""}
              onChange={handleChange}
              rows={4}
              placeholder="Add any additional notes or information"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Timezone
            </label>
            <input
              type="text"
              name="timezone"
              placeholder="e.g., America/New_York"
              value={syncedFormData.timezone || ""}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Nickname
            </label>
            <input
              type="text"
              name="nickname"
              value={syncedFormData.nickname || ""}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Website
            </label>
            <input
              type="url"
              name="website"
              placeholder="https://example.com"
              value={syncedFormData.website || ""}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-lg bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-400"
      >
        {isSubmitting ? (
          <div className="flex items-center justify-center">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            <span>Saving...</span>
          </div>
        ) : (
          "Save Home Details"
        )}
      </button>
    </form>
  );
};

export default HomeInfo;
